import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, HardHat, Globe } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { track, imgUrl } from "../lib/api";

const NAV = [
  { to: "/", key: "home" },
  { to: "/about", key: "about" },
  { to: "/services", key: "services" },
  { to: "/projects", key: "projects" },
  { to: "/before-after", key: "ba" },
  { to: "/reviews", key: "reviews" },
  { to: "/contact", key: "contact" },
];

export const formatPhone = (p) => {
  const d = (p || "").replace(/\D/g, "");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p;
};

export const Logo = ({ light = false }) => {
  const { settings } = useSite();
  return (
    <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5 group">
      {settings.logo_url ? (
        light ? (
          <span className="bg-white px-2.5 py-1.5 rounded-sm inline-flex items-center">
            <img src={imgUrl(settings.logo_url)} alt="Noyola Construction LLC" className="h-8 md:h-9 w-auto object-contain" />
          </span>
        ) : (
          <img src={imgUrl(settings.logo_url)} alt="Noyola Construction LLC" className="h-11 md:h-12 w-auto object-contain" />
        )
      ) : (
        <>
          <span className="w-10 h-10 bg-[#005AE0] flex items-center justify-center">
            <HardHat className="w-5 h-5 text-white" strokeWidth={2.2} />
          </span>
          <span className={`font-heading leading-none ${light ? "text-white" : "text-[#0B1B3D]"}`}>
            <span className="block text-[15px] font-800 font-extrabold tracking-tight">NOYOLA</span>
            <span className="block text-[10px] font-semibold tracking-[0.28em] opacity-70">CONSTRUCTION LLC</span>
          </span>
        </>
      )}
    </Link>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { settings, lang, setLang, L } = useSite();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const dark = pathname === "/" && !scrolled && !open;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        dark ? "bg-transparent" : "bg-white/95 backdrop-blur-md border-b border-slate-200"
      }`}
    >
      {settings.announcement && (
        <div className="bg-[#005AE0] text-white text-center text-xs md:text-sm font-semibold py-2 px-4" data-testid="announcement-bar">
          {settings.announcement}
        </div>
      )}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[76px] flex items-center justify-between">
        <Logo light={dark} />
        <nav className="hidden xl:flex items-center gap-4 2xl:gap-6">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) =>
                `font-heading text-[12px] font-semibold uppercase tracking-[0.08em] whitespace-nowrap transition-colors duration-200 ${
                  dark ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-[#0B1B3D]"
                } ${isActive ? (dark ? "!text-white" : "!text-[#005AE0]") : ""}`
              }
            >
              {L.nav[n.key]}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setLang(lang === "en" ? "es" : "en")}
            data-testid="language-toggle"
            className={`flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest px-2.5 py-2 border transition-colors ${
              dark ? "text-white border-white/40 hover:bg-white/10" : "text-[#0B1B3D] border-slate-300 hover:border-[#005AE0]"}`}
            aria-label="Switch language"
          >
            <Globe className="w-3.5 h-3.5" /> {lang === "en" ? "ES" : "EN"}
          </button>
          <a
            href={`tel:${settings.phone || ""}`}
            data-testid="header-call-btn"
            onClick={() => track("phone_click", { location: "header" })}
            className={`hidden 2xl:flex items-center gap-2 font-heading font-bold text-sm whitespace-nowrap ${dark ? "text-white" : "text-[#0B1B3D]"}`}
          >
            <Phone className="w-4 h-4 text-[#005AE0]" />
            {formatPhone(settings.phone)}
          </a>
          <Link to="/estimate" data-testid="header-estimate-btn" className="hidden sm:inline-flex btn-primary !py-3 !px-5 !text-xs">
            {L.header.freeEstimate}
          </Link>
          <a
            href={`tel:${settings.phone || ""}`}
            data-testid="mobile-call-btn"
            onClick={() => track("phone_click", { location: "mobile_header" })}
            className="2xl:hidden w-11 h-11 flex items-center justify-center bg-[#005AE0] text-white"
            aria-label="Call now"
          >
            <Phone className="w-5 h-5" />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className={`xl:hidden w-11 h-11 flex items-center justify-center ${dark ? "text-white" : "text-[#0B1B3D]"}`}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden bg-white border-t border-slate-200 px-5 py-6 flex flex-col gap-1" data-testid="mobile-menu">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className="font-heading font-bold text-lg text-[#0B1B3D] py-3 border-b border-slate-100">
              {L.nav[n.key]}
            </NavLink>
          ))}
          <Link to="/estimate" data-testid="mobile-estimate-btn" className="btn-primary mt-4 w-full">
            {L.header.requestFreeEstimate}
          </Link>
        </div>
      )}
    </header>
  );
};
